import { createFileRoute } from '@tanstack/react-router'
import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import {
  UploadIcon,
  CheckCircleIcon,
  XCircleIcon,
  AlertCircleIcon,
  FileTextIcon,
  RefreshCwIcon,
  EyeIcon,
  ArrowRightIcon,
  CheckIcon,
  XIcon,
  TableIcon,
  Loader2Icon,
  DownloadIcon,
  HistoryIcon,
  ChevronDownIcon,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { BRAND_MINT, cn } from '@/lib/utils'
import {
  getCsvImportFieldsApi,
  uploadCsvImportApi,
  confirmCsvImportApi,
  getCsvImportHistoryApi,
  type CsvImportField,
  type CsvImportMatchedRow,
  type CsvImportHistoryEntry,
} from '@/lib/api'

export const Route = createFileRoute('/dashboard/csv-import')({
  component: CsvImportPage,
})

type Step = 'upload' | 'mapping' | 'preview' | 'confirming' | 'done'

function CsvImportPage() {
  // ── State ──────────────────────────────────────────────────────────
  const [step, setStep] = useState<Step>('upload')
  const [fields, setFields] = useState<CsvImportField[]>([])
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [confirming, setConfirming] = useState(false)

  // Upload state
  const [file, setFile] = useState<File | null>(null)
  const [dragOver, setDragOver] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Column mapping state
  const [csvColumns, setCsvColumns] = useState<string[]>([])
  const [columnMapping, setColumnMapping] = useState<Record<string, string>>({})

  // Preview state
  const [uploadId, setUploadId] = useState<string | null>(null)
  const [matchedRows, setMatchedRows] = useState<CsvImportMatchedRow[]>([])
  const [matchedCount, setMatchedCount] = useState(0)
  const [unmatchedCount, setUnmatchedCount] = useState(0)
  const [totalRows, setTotalRows] = useState(0)

  // Result state
  const [result, setResult] = useState<{
    message: string
    total_marked: number
    total_errors: number
    cycles_fully_settled: number
    results: Array<{
      payer_id: number
      status: string
      amount?: string
      cycle_settled?: boolean
      error?: string
      reason?: string
    }>
  } | null>(null)

  // Import history state
  const [history, setHistory] = useState<CsvImportHistoryEntry[]>([])
  const [historyLoading, setHistoryLoading] = useState(true)
  const [historyOpen, setHistoryOpen] = useState(false)

  // Load recognised fields
  useEffect(() => {
    getCsvImportFieldsApi()
      .then((res) => setFields(res.fields))
      .catch((err) => toast.error(err instanceof Error ? err.message : 'Failed to load import fields'))
      .finally(() => setLoading(false))
  }, [])

  // Load import history
  const fetchHistory = useCallback(async () => {
    setHistoryLoading(true)
    try {
      const res = await getCsvImportHistoryApi({ limit: 10 })
      setHistory(res.data)
    } catch {
      // Swallow — history is non-critical
    } finally {
      setHistoryLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchHistory()
  }, [fetchHistory])

  // ── File handling ──────────────────────────────────────────────────

  const handleFileDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    const droppedFile = e.dataTransfer.files[0]
    if (droppedFile && droppedFile.name.endsWith('.csv')) {
      setFile(droppedFile)
      setStep('mapping')
      setCsvColumns([])
      setColumnMapping({})
      setResult(null)
    } else {
      toast.error('Only CSV files are supported.')
    }
  }, [])

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0]
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.csv')) {
        toast.error('Only CSV files are supported.')
        return
      }
      setFile(selectedFile)
      setStep('mapping')
      setCsvColumns([])
      setColumnMapping({})
      setResult(null)
    }
  }, [])

  // ── Column mapping ─────────────────────────────────────────────────

  const setMapping = (fieldKey: string, csvColumn: string) => {
    setColumnMapping((prev) => {
      const next = { ...prev }

      // Remove the old mapping for this field key
      for (const [col, key] of Object.entries(next)) {
        if (key === fieldKey) {
          delete next[col]
          break
        }
      }

      // If setting a new column, and it's not empty
      if (csvColumn) {
        next[csvColumn] = fieldKey
      }

      return next
    })
  }

  // ── Upload & Preview ──────────────────────────────────────────────

  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select a CSV file.')
      return
    }

    setUploading(true)
    try {
      const result = await uploadCsvImportApi(file, columnMapping)
      setUploadId(result.upload_id)
      setCsvColumns(result.columns)
      setMatchedRows(result.matched_rows)
      setMatchedCount(result.matched_count)
      setUnmatchedCount(result.unmatched_count)
      setTotalRows(result.total_rows)
      setStep('preview')

      if (result.matched_count === 0) {
        toast.error('No matching payer obligations found. Check your column mapping.')
      } else {
        toast.success(`Found ${result.matched_count} matching payer obligation(s).`)
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to upload file.')
    } finally {
      setUploading(false)
    }
  }

  // ── Confirm ────────────────────────────────────────────────────────

  const handleConfirm = async () => {
    if (!uploadId) return

    setConfirming(true)
    try {
      const res = await confirmCsvImportApi({
        upload_id: uploadId,
        payer_ids: matchedRows.map((r) => r.payment_cycle_payer_id),
      })
      setResult({
        message: res.message,
        total_marked: res.total_marked,
        total_errors: res.total_errors,
        cycles_fully_settled: res.cycles_fully_settled,
        results: res.results,
      })
      setStep('done')
      toast.success(res.message)
      // Refresh the history list to include this import
      fetchHistory()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to confirm import.')
    } finally {
      setConfirming(false)
    }
  }

  const resetAll = () => {
    setStep('upload')
    setFile(null)
    setCsvColumns([])
    setColumnMapping({})
    setUploadId(null)
    setMatchedRows([])
    setMatchedCount(0)
    setUnmatchedCount(0)
    setTotalRows(0)
    setResult(null)
  }

  // ── Parse CSV headers for column mapping purpose (client-side read) ─

  const handleReadCsvHeaders = useCallback(async () => {
    if (!file) return

    try {
      const text = await file.text()
      const firstLine = text.split('\n')[0]
      const columns = firstLine.split(',').map((c) => c.trim().replace(/^"|"$/g, ''))
      setCsvColumns(columns)

      // Auto-detect mappings
      const detected: Record<string, string> = {}
      for (const col of columns) {
        const lower = col.toLowerCase().trim()

        // Try to auto-map by common names
        if (lower.includes('payer') && lower.includes('id')) {
          detected[col] = 'payment_cycle_payer_id'
        } else if (lower.includes('circle') || lower.includes('pot') || lower === 'request id') {
          detected[col] = 'circle_req_id'
        } else if (lower.includes('cycle')) {
          detected[col] = 'cycle_number'
        } else if (lower.includes('email')) {
          detected[col] = 'employee_email'
        }
      }

      setColumnMapping(detected)
      if (Object.keys(detected).length > 0) {
        toast.success(`Auto-detected ${Object.keys(detected).length} column mapping(s).`)
      }
    } catch {
      // Silent — user can map manually
    }
  }, [file])

  useEffect(() => {
    if (file && step === 'mapping') {
      handleReadCsvHeaders()
    }
  }, [file, step, handleReadCsvHeaders])

  // ── Download CSV template ──────────────────────────────────────────

  const handleDownloadTemplate = useCallback(() => {
    const headers = [
      'Payment Cycle Payer ID',
      'Circle / Request ID',
      'Cycle Number',
      'Employee Email',
    ]

    const sampleRows = [
      ['123', '42', '1', 'employee@company.com'],
      ['124', '42', '2', 'another@company.com'],
      ['', '55', '1', 'third@company.com'],
    ]

    const escapeCsv = (val: string) => {
      if (val.includes(',') || val.includes('"') || val.includes('\n')) {
        return `"${val.replace(/"/g, '""')}"`
      }
      return val
    }

    const csvContent = [
      headers.join(','),
      ...sampleRows.map((row) => row.map(escapeCsv).join(',')),
    ].join('\r\n') + '\r\n'

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'payrun-import-template.csv'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }, [])

  // ── Render helpers ─────────────────────────────────────────────────

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'paid':
      case 'marked_paid':
        return (
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-medium text-emerald-600 dark:text-emerald-400">
            <CheckCircleIcon className="size-3" />
            Paid
          </span>
        )
      case 'skipped':
        return (
          <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 px-2 py-0.5 text-[10px] font-medium text-amber-600 dark:text-amber-400">
            <AlertCircleIcon className="size-3" />
            Skipped
          </span>
        )
      case 'error':
        return (
          <span className="inline-flex items-center gap-1 rounded-full bg-red-500/10 px-2 py-0.5 text-[10px] font-medium text-red-600 dark:text-red-400">
            <XCircleIcon className="size-3" />
            Error
          </span>
        )
      default:
        return (
          <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
            {status}
          </span>
        )
    }
  }

  // ── Render ─────────────────────────────────────────────────────────

  return (
    <div className="mx-auto max-w-5xl">
      <div className="mb-8 flex items-start justify-between">
        <div className="flex items-start gap-3">
          <span
            aria-hidden="true"
            className="mt-0.5 shrink-0"
            style={{ width: '2px', height: '28px', backgroundColor: BRAND_MINT }}
          />
          <div>
            <h1 className="text-2xl font-semibold">Import payrun</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Upload a CSV file from your payroll platform to mark payment cycles as paid.
              Map your CSV columns to our recognised fields, preview the matches, then confirm.
            </p>
          </div>
        </div>
      </div>

      {/* ── Step indicator ── */}
      <div className="mb-8 flex items-center gap-2 text-xs font-medium">
        {(['upload', 'mapping', 'preview', 'done'] as const).map((s, i) => {
          const isActive = step === s
          const isPast = ['upload', 'mapping', 'preview'].indexOf(step) > i || step === 'done'
          return (
            <div key={s} className="flex items-center gap-2">
              <span
                className={cn(
                  'flex size-6 items-center justify-center rounded-full text-[10px] font-semibold transition-colors',
                  isActive
                    ? 'bg-primary text-primary-foreground'
                    : isPast
                      ? 'bg-emerald-500/20 text-emerald-600 dark:text-emerald-400'
                      : 'bg-muted text-muted-foreground/50',
                )}
              >
                {isPast ? <CheckIcon className="size-3" /> : i + 1}
              </span>
              <span
                className={cn(
                  'capitalize',
                  isActive ? 'text-foreground font-semibold' : 'text-muted-foreground/50',
                )}
              >
                {s === 'upload' ? 'Upload' : s === 'mapping' ? 'Map columns' : s === 'preview' ? 'Preview' : 'Done'}
              </span>
              {i < 3 && <ArrowRightIcon className="size-3 text-muted-foreground/30" />}
            </div>
          )
        })}
      </div>

      {/* ── Step: Upload ── */}
      {step === 'upload' && (
        <div className="space-y-6">
          {/* Drop zone */}
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleFileDrop}
            onClick={() => fileInputRef.current?.click()}
            className={cn(
              'flex cursor-pointer flex-col items-center gap-4 rounded-xl border-2 border-dashed p-12 transition-colors',
              dragOver
                ? 'border-primary bg-primary/5'
                : 'border-border hover:border-muted-foreground/30 hover:bg-muted/20',
            )}
          >
            <div className="flex size-16 items-center justify-center rounded-full bg-muted">
              <UploadIcon className="size-8 text-muted-foreground/60" />
            </div>
            <div className="text-center">
              <p className="text-sm font-medium">Drop your CSV file here, or click to browse</p>
              <p className="mt-1 text-xs text-muted-foreground">
                The CSV should contain payer IDs, circle IDs, or cycle numbers from your payrun export
              </p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              className="hidden"
              onChange={handleFileSelect}
            />
            <Button variant="outline" size="sm" className="pointer-events-none">
              <FileTextIcon className="size-4" />
              Select CSV file
            </Button>
          </div>

          {/* Help card */}
          <div className="rounded-xl border border-border bg-card p-4">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <h3 className="mb-2 text-sm font-semibold">How it works</h3>
                <ol className="ml-4 list-decimal space-y-1.5 text-xs text-muted-foreground">
                  <li>Export a payrun CSV from your payroll platform (or use the <strong>Generate payrun</strong> tool)</li>
                  <li>Upload the CSV here — we'll read the column headers</li>
                  <li>Map your CSV columns to our recognised fields (Payer ID, Circle ID, etc.)</li>
                  <li>Preview which payer obligations will be marked as paid</li>
                  <li>Confirm to mark them as paid in the system</li>
                </ol>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleDownloadTemplate}
                className="shrink-0 gap-1.5"
              >
                <DownloadIcon className="size-3.5" />
                Template CSV
              </Button>
            </div>
          </div>

          {/* Import history */}
          <div className="rounded-xl border border-border bg-card overflow-hidden">
            <button
              type="button"
              onClick={() => setHistoryOpen(!historyOpen)}
              className="flex w-full items-center justify-between px-4 py-3 text-left transition-colors hover:bg-muted/30"
            >
              <div className="flex items-center gap-2">
                <HistoryIcon className="size-4 text-muted-foreground" />
                <span className="text-sm font-semibold">Import history</span>
                <span className="text-xs text-muted-foreground">
                  {historyLoading ? '…' : `(${history.length})`}
                </span>
              </div>
              <ChevronDownIcon
                className={cn(
                  'size-4 text-muted-foreground transition-transform',
                  historyOpen && 'rotate-180',
                )}
              />
            </button>

            {historyOpen && (
              <div className="border-t border-border">
                {historyLoading ? (
                  <div className="flex items-center justify-center py-6">
                    <Loader2Icon className="size-4 animate-spin text-muted-foreground" />
                  </div>
                ) : history.length === 0 ? (
                  <p className="px-4 py-6 text-center text-xs text-muted-foreground">No import history yet.</p>
                ) : (
                  <div className="divide-y divide-border">
                    {history.map((entry) => (
                      <div key={entry.import_run_id} className="flex items-center justify-between px-4 py-2.5">
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium">{entry.original_filename}</p>
                          <p className="text-[10px] text-muted-foreground">
                            {new Date(entry.completed_at).toLocaleDateString()} ·{' '}
                            {entry.total_rows} row{entry.total_rows !== 1 ? 's' : ''} ·{' '}
                            <span className="text-emerald-600 dark:text-emerald-400">{entry.marked_count} paid</span>
                            {entry.error_count > 0 && (
                              <> · <span className="text-amber-600 dark:text-amber-400">{entry.error_count} error{entry.error_count !== 1 ? 's' : ''}</span></>
                            )}
                            {entry.cycles_settled > 0 && (
                              <> · {entry.cycles_settled} cycle{entry.cycles_settled !== 1 ? 's' : ''} settled</>
                            )}
                          </p>
                        </div>
                        <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                          {entry.matched_count} matched
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Step: Column Mapping ── */}
      {step === 'mapping' && file && (
        <div className="space-y-6">
          {/* File info */}
          <div className="rounded-xl border border-border bg-card p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FileTextIcon className="size-5 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">{file.name}</p>
                  <p className="text-xs text-muted-foreground">{(file.size / 1024).toFixed(1)} KB</p>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => { setFile(null); setStep('upload'); setCsvColumns([]) }}>
                <XIcon className="size-4" />
                Change file
              </Button>
            </div>
          </div>

          {/* Column mapping */}
          <div className="rounded-xl border border-border bg-card p-4">
            <h2 className="mb-4 text-sm font-semibold">Column mapping</h2>
            <p className="mb-4 text-xs text-muted-foreground">
              Tell us which column in your CSV corresponds to each recognised field.
              At minimum, map <strong>Payment Cycle Payer ID</strong> — or use Circle ID + Cycle Number.
            </p>

            <div className="space-y-3">
              {fields.map((field) => {
                const mappedColumn = Object.entries(columnMapping).find(
                  ([_, v]) => v === field.key,
                )?.[0]

                return (
                  <div key={field.key} className="flex items-center gap-3">
                    <div className="min-w-0 flex-1">
                      <Label className="text-xs font-medium">{field.label}</Label>
                      <p className="text-[10px] text-muted-foreground">{field.description}</p>
                    </div>
                    <ArrowRightIcon className="size-3 shrink-0 text-muted-foreground/40" />
                    <select
                      value={mappedColumn || ''}
                      onChange={(e) => setMapping(field.key, e.target.value)}
                      className="h-9 w-48 rounded-lg border border-border bg-background px-3 text-xs focus:outline-none focus:ring-2 focus:ring-primary/30"
                    >
                      <option value="">— Not mapped —</option>
                      {csvColumns.map((col) => (
                        <option key={col} value={col}>
                          {col}
                        </option>
                      ))}
                    </select>
                  </div>
                )
              })}
            </div>

            {/* Detected columns summary */}
            {csvColumns.length > 0 && (
              <div className="mt-4 border-t border-border pt-3">
                <p className="mb-1.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                  Detected CSV columns
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {csvColumns.map((col) => {
                    const isMapped = Object.keys(columnMapping).includes(col)
                    return (
                      <span
                        key={col}
                        className={cn(
                          'rounded-md border px-2 py-0.5 text-[10px] font-mono transition-colors',
                          isMapped
                            ? 'border-emerald-300 bg-emerald-500/10 text-emerald-600 dark:border-emerald-700 dark:text-emerald-400'
                            : 'border-border text-muted-foreground',
                        )}
                      >
                        {col}
                        {isMapped && <CheckCircleIcon className="ml-1 inline size-2.5" />}
                      </span>
                    )
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-3">
            <Button onClick={handleUpload} disabled={uploading}>
              {uploading ? (
                <><Loader2Icon className="size-4 animate-spin" /> Uploading & matching…</>
              ) : (
                <><EyeIcon className="size-4" /> Preview matches</>
              )}
            </Button>
            <Button variant="ghost" onClick={() => { setFile(null); setStep('upload'); setCsvColumns([]) }}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* ── Step: Preview ── */}
      {step === 'preview' && (
        <div className="space-y-6">
          {/* Summary cards */}
          <div className="grid grid-cols-3 gap-4">
            <div className="rounded-xl border border-border bg-card p-4">
              <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Total rows</p>
              <p className="mt-1 text-2xl font-semibold">{totalRows}</p>
            </div>
            <div className="rounded-xl border border-emerald-200 bg-emerald-500/5 p-4 dark:border-emerald-800">
              <p className="text-[10px] font-medium uppercase tracking-wider text-emerald-600 dark:text-emerald-400">
                Matched
              </p>
              <p className="mt-1 text-2xl font-semibold text-emerald-600 dark:text-emerald-400">
                {matchedCount}
              </p>
            </div>
            <div className="rounded-xl border border-amber-200 bg-amber-500/5 p-4 dark:border-amber-800">
              <p className="text-[10px] font-medium uppercase tracking-wider text-amber-600 dark:text-amber-400">
                Unmatched
              </p>
              <p className="mt-1 text-2xl font-semibold text-amber-600 dark:text-amber-400">
                {unmatchedCount}
              </p>
            </div>
          </div>

          {/* Matched rows table */}
          {matchedRows.length > 0 && (
            <div className="rounded-xl border border-border bg-card overflow-hidden">
              <div className="border-b border-border px-4 py-3">
                <div className="flex items-center gap-2">
                  <TableIcon className="size-4 text-muted-foreground" />
                  <span className="text-sm font-semibold">Matched payer obligations</span>
                  <span className="text-xs text-muted-foreground">
                    ({matchedRows.length} row{matchedRows.length !== 1 ? 's' : ''})
                  </span>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-xs">
                  <thead>
                    <tr className="border-b border-border bg-muted/30">
                      <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Payer ID</th>
                      <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Circle ID</th>
                      <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Cycle</th>
                      <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Due date</th>
                      <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Amount</th>
                      <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Recipient</th>
                      <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {matchedRows.map((row) => (
                      <tr key={row.payment_cycle_payer_id} className="transition-colors hover:bg-muted/20">
                        <td className="px-3 py-2 font-mono">{row.payment_cycle_payer_id}</td>
                        <td className="px-3 py-2 font-mono">{row.circle_req_id}</td>
                        <td className="px-3 py-2">{row.cycle_number}</td>
                        <td className="px-3 py-2">{new Date(row.due_date).toLocaleDateString()}</td>
                        <td className="px-3 py-2 font-mono">{row.amount}</td>
                        <td className="px-3 py-2">{row.recipient_name}</td>
                        <td className="px-3 py-2">{getStatusBadge(row.current_status)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Action buttons */}
          <div className="flex items-center gap-3">
            <Button
              onClick={handleConfirm}
              disabled={confirming || matchedCount === 0}
              className="gap-1.5"
            >
              {confirming ? (
                <><Loader2Icon className="size-4 animate-spin" /> Marking as paid…</>
              ) : (
                <><CheckCircleIcon className="size-4" /> Mark {matchedCount} obligation{matchedCount !== 1 ? 's' : ''} as paid</>
              )}
            </Button>
            <Button variant="outline" onClick={() => setStep('mapping')} className="gap-1.5">
              <RefreshCwIcon className="size-4" />
              Back to mapping
            </Button>
            <Button variant="ghost" onClick={resetAll} className="text-muted-foreground">
              Start over
            </Button>
          </div>

          {/* Warning if unmatched */}
          {unmatchedCount > 0 && (
            <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-500/5 p-3 dark:border-amber-800">
              <AlertCircleIcon className="mt-0.5 size-4 shrink-0 text-amber-600 dark:text-amber-400" />
              <div>
                <p className="text-xs font-medium text-amber-600 dark:text-amber-400">
                  {unmatchedCount} row{unmatchedCount !== 1 ? 's' : ''} could not be matched
                </p>
                <p className="mt-0.5 text-[10px] text-amber-600/70 dark:text-amber-400/70">
                  Rows without a matching payer obligation were skipped. Check that your CSV contains
                  valid Payer IDs, Circle IDs, or Cycle Numbers that belong to your organization's
                  pending obligations.
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Step: Done ── */}
      {step === 'done' && result && (
        <div className="space-y-6">
          {/* Result summary */}
          <div className="grid grid-cols-4 gap-4">
            <div className="rounded-xl border border-emerald-200 bg-emerald-500/5 p-4 dark:border-emerald-800">
              <p className="text-[10px] font-medium uppercase tracking-wider text-emerald-600 dark:text-emerald-400">
                Marked paid
              </p>
              <p className="mt-1 text-2xl font-semibold text-emerald-600 dark:text-emerald-400">
                {result.total_marked}
              </p>
            </div>
            <div className="rounded-xl border border-amber-200 bg-amber-500/5 p-4 dark:border-amber-800">
              <p className="text-[10px] font-medium uppercase tracking-wider text-amber-600 dark:text-amber-400">
                Errors
              </p>
              <p className="mt-1 text-2xl font-semibold text-amber-600 dark:text-amber-400">
                {result.total_errors}
              </p>
            </div>
            <div className="rounded-xl border border-blue-200 bg-blue-500/5 p-4 dark:border-blue-800">
              <p className="text-[10px] font-medium uppercase tracking-wider text-blue-600 dark:text-blue-400">
                Cycles settled
              </p>
              <p className="mt-1 text-2xl font-semibold text-blue-600 dark:text-blue-400">
                {result.cycles_fully_settled}
              </p>
            </div>
            <div className="rounded-xl border border-border bg-card p-4">
              <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Processed</p>
              <p className="mt-1 text-2xl font-semibold">{result.total_marked + result.total_errors}</p>
            </div>
          </div>

          {/* Result rows */}
          <div className="rounded-xl border border-border bg-card overflow-hidden">
            <div className="border-b border-border px-4 py-3">
              <h2 className="text-sm font-semibold">Import results</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-xs">
                <thead>
                  <tr className="border-b border-border bg-muted/30">
                    <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Payer ID</th>
                    <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Status</th>
                    <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Amount</th>
                    <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Cycle settled</th>
                    <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Details</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {result.results.map((r) => (
                    <tr key={r.payer_id} className="transition-colors hover:bg-muted/20">
                      <td className="px-3 py-2 font-mono">{r.payer_id}</td>
                      <td className="px-3 py-2">{getStatusBadge(r.status)}</td>
                      <td className="px-3 py-2 font-mono">{r.amount || '—'}</td>
                      <td className="px-3 py-2">
                        {r.cycle_settled ? (
                          <CheckCircleIcon className="size-4 text-emerald-500" />
                        ) : (
                          <span className="text-muted-foreground/50">—</span>
                        )}
                      </td>
                      <td className="max-w-[200px] truncate px-3 py-2 text-muted-foreground">
                        {r.error || r.reason || (r.status === 'paid' ? 'Marked as paid' : '')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Success message */}
          {result.total_errors === 0 && (
            <div className="flex items-start gap-2 rounded-xl border border-emerald-200 bg-emerald-500/5 p-4 dark:border-emerald-800">
              <CheckCircleIcon className="mt-0.5 size-5 shrink-0 text-emerald-600 dark:text-emerald-400" />
              <div>
                <p className="text-sm font-medium text-emerald-700 dark:text-emerald-300">Import completed successfully</p>
                <p className="mt-0.5 text-xs text-emerald-600/70 dark:text-emerald-400/70">
                  {result.total_marked} payer obligation{result.total_marked !== 1 ? 's' : ''} marked as paid.
                  {result.cycles_fully_settled > 0 && ` ${result.cycles_fully_settled} cycle(s) fully settled.`}
                </p>
              </div>
            </div>
          )}

          {result.total_errors > 0 && (
            <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-500/5 p-4 dark:border-amber-800">
              <AlertCircleIcon className="mt-0.5 size-5 shrink-0 text-amber-600 dark:text-amber-400" />
              <div>
                <p className="text-sm font-medium text-amber-700 dark:text-amber-300">Some obligations could not be marked</p>
                <p className="mt-0.5 text-xs text-amber-600/70 dark:text-amber-400/70">
                  {result.total_marked} marked, {result.total_errors} error{result.total_errors !== 1 ? 's' : ''}.
                  These may already have been paid or belong to another organization.
                </p>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center gap-3">
            <Button onClick={resetAll} className="gap-1.5">
              <UploadIcon className="size-4" />
              Import another file
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
